import React, { type ReactNode, useEffect, useMemo } from 'react'
import { useCollectionData, useDocumentData } from 'react-firebase-hooks/firestore'

import { captureException, captureMessage, setContext } from '@sentry/react'
import { limit, query, where } from 'firebase/firestore'

import { AppLoading } from '@/components/organisms/AppLoading/AppLoading.tsx'

import { regionCollection, regionDocument, type RegionSnapshotData } from '../firestore/regionCollection.ts'

import { DashboardAlertProvider } from './DashboardAlertProvider.tsx'
import { NO_OPERATING_EVENING_ID, NotificationSoundProvider } from './NotificationSoundProvider.tsx'
import { TeamsLatestLocationProvider } from './TeamsLatestLocationProvider.tsx'
import { TeamsProvider } from './TeamsProvider.tsx'
import { TransportRequestsProvider } from './TransportRequestsProvider.tsx'

export interface RegionContextValue {
  readonly region?: RegionSnapshotData
}

export const RegionContext = React.createContext<RegionContextValue>({})

export interface RegionProviderProps {
  regionId?: string | null
  centralId?: number
  children: ReactNode
}

const regionFromCentralIdQuery = (piloteId?: number) =>
  query(
    regionCollection(),
    limit(1),
    ...(!piloteId || Number.isNaN(piloteId) ? [] : [where('piloteId', '==', piloteId)])
  )

export const RegionProvider: React.FC<RegionProviderProps> = ({ regionId, centralId, children }) => {
  const [regionFromRegionId, , errorRegionFromRegionId] = useDocumentData<RegionSnapshotData>(
    regionId ? regionDocument(regionId) : null
  )
  const [regionFromCentralId, , errorRegionFromCentralId, querySnapshot] = useCollectionData<RegionSnapshotData>(
    !regionId ? regionFromCentralIdQuery(centralId) : null
  )
  const contextValue = useMemo(
    () => ({ region: regionFromRegionId ?? regionFromCentralId?.[0] }),
    [regionFromRegionId, regionFromCentralId]
  )
  const { region } = contextValue
  const regionNotFound = centralId !== undefined && querySnapshot?.empty

  useEffect(() => {
    if (regionNotFound) {
      captureMessage(`Pilote Central ${centralId} does not have firebase region`, {
        tags: { 'firestore.document.snapshot': 'regions' },
        fingerprint: ['firestore', 'regions', String(centralId)],
        extra: { pilote: { centralId } },
      })
    }
  }, [regionNotFound, centralId])

  useEffect(() => {
    if (errorRegionFromRegionId) {
      captureException(errorRegionFromRegionId, {
        tags: { 'firestore.document.snapshot': 'regions' },
        fingerprint: ['firestore', 'document', 'regions'],
        extra: { document: { regionId } },
      })
    }
  }, [errorRegionFromRegionId, regionId])

  useEffect(() => {
    if (errorRegionFromCentralId) {
      captureException(errorRegionFromCentralId, {
        tags: { 'firestore.query.snapshot': 'regions' },
        fingerprint: ['firestore', 'query', 'regions'],
        extra: { document: { centralId } },
      })
    }
  }, [errorRegionFromCentralId, centralId])

  useEffect(() => {
    if (region?._id) {
      setContext('character', {
        name: 'ONR Region',
        regionId: region._id,
        region: region.name,
      })
    }
  }, [region?._id, region?.name])

  if (regionNotFound) {
    return <AppLoading />
  }

  if (!region?.currentOperatingDate) {
    return region ? (
      <RegionContext.Provider value={contextValue}>
        <DashboardAlertProvider regionId={region._id} eveningId={0}>
          <NotificationSoundProvider regionId={region._id} eveningId={NO_OPERATING_EVENING_ID}>
            {children}
          </NotificationSoundProvider>
        </DashboardAlertProvider>
      </RegionContext.Provider>
    ) : (
      <RegionContext.Provider value={contextValue}>{children}</RegionContext.Provider>
    )
  }

  return (
    <RegionContext.Provider value={contextValue}>
      <TeamsProvider regionId={region._id} eveningId={region.currentOperatingDate.eveningId}>
        <TeamsLatestLocationProvider>
          <TransportRequestsProvider regionId={region._id} eveningId={region.currentOperatingDate.eveningId}>
            <DashboardAlertProvider regionId={region._id} eveningId={region.currentOperatingDate.eveningId}>
              <NotificationSoundProvider regionId={region._id} eveningId={region.currentOperatingDate.eveningId}>
                {children}
              </NotificationSoundProvider>
            </DashboardAlertProvider>
          </TransportRequestsProvider>
        </TeamsLatestLocationProvider>
      </TeamsProvider>
    </RegionContext.Provider>
  )
}
